import type { Account } from '../types/account.js';
import type { FranchiseLocationProfile } from '../types/franchiseLocationProfile.js';
import type { FranchiseProfile } from '../types/franchiseProfile.js';

// Client-side mirror of server/lib/templating.ts. Keep in sync with the
// server helper — both must resolve the same placeholder set, the same case
// policy, the {gymInfo} fallback chain, and the new (Pre-P2b-3 §1)
// {locationName} / {campaignDescriptor} / {monthYear} placeholders.
//
// Authoring-time use only: the server is still the source of truth at
// AccountCampaign snapshot / P2b-3 render. The client resolver exists so the
// row editor's read-mode preview can show what an ad will actually look like
// for the location being edited, without a network round-trip.
//
// Resolution rules (identical to server):
// - Unknown placeholders are LEFT AS LITERAL TEXT so typos are discoverable.
// - Placeholder names are CASE-SENSITIVE.
// - If a location placeholder is required and no FranchiseLocationProfile is
//   provided, the placeholder is left literal and listed in
//   missingPlaceholders. (The server also logs a warning; the client does
//   not — the UI surfaces missing values visually.)
// - {gymInfo} resolution order:
//     1. FranchiseLocationProfile.gymInfo (if non-empty)
//     2. FranchiseProfile.defaultGymInfo (brand-level fallback, if non-empty)
//     3. leave literal + report
// - {locationName} is a synonym for {accountName} (same resolution: account.name).
//   Both spellings are supported so Meta naming templates can use either.
// - {campaignDescriptor} resolves from extras.campaignDescriptor. If not
//   provided in extras (or null/empty), left literal + reported.
// - {monthYear} resolves from extras.monthYear (a pre-formatted string like
//   "Apr 26"). The format and date-precedence rule live at the call site
//   (src/lib/monthYear.ts) — this helper just substitutes.

const PLACEHOLDER = /\{([a-zA-Z]+)\}/g;

type Resolver = (placeholder: string) => string | null;

export type RenderExtras = {
  campaignDescriptor?: string | null;
  monthYear?: string | null;
};

function buildResolver(
  account: Pick<Account, 'name' | 'slug'>,
  locationProfile: FranchiseLocationProfile | null,
  franchiseProfile: FranchiseProfile | null,
  extras: RenderExtras,
  onMissing: (placeholder: string) => void,
): Resolver {
  return (placeholder) => {
    switch (placeholder) {
      case 'accountName':
      case 'locationName':
        return account.name;
      case 'slug':
        return account.slug;
      case 'suburb':
        if (!locationProfile) {
          onMissing(placeholder);
          return null;
        }
        return locationProfile.franchiseSuburb;
      case 'shortSuburb':
        if (!locationProfile) {
          onMissing(placeholder);
          return null;
        }
        return locationProfile.franchiseSuburbShort ?? locationProfile.franchiseSuburb;
      case 'suburbSlug':
        if (!locationProfile) {
          onMissing(placeholder);
          return null;
        }
        return locationProfile.franchiseSuburbSlug;
      case 'state':
        if (!locationProfile) {
          onMissing(placeholder);
          return null;
        }
        return locationProfile.state;
      case 'gymInfo': {
        const locationValue = locationProfile?.gymInfo ?? null;
        if (locationValue !== null && locationValue.length > 0) return locationValue;
        const brandValue = franchiseProfile?.defaultGymInfo ?? null;
        if (brandValue !== null && brandValue.length > 0) return brandValue;
        onMissing(placeholder);
        return null;
      }
      case 'campaignDescriptor': {
        const value = extras.campaignDescriptor ?? null;
        if (value !== null && value.length > 0) return value;
        onMissing(placeholder);
        return null;
      }
      case 'monthYear': {
        const value = extras.monthYear ?? null;
        if (value !== null && value.length > 0) return value;
        onMissing(placeholder);
        return null;
      }
      default:
        return null;
    }
  };
}

export type RenderResult = {
  output: string;
  missingPlaceholders: string[];
};

export function renderTemplate(
  template: string,
  account: Pick<Account, 'name' | 'slug'>,
  locationProfile: FranchiseLocationProfile | null,
  franchiseProfile: FranchiseProfile | null = null,
  extras: RenderExtras = {},
): RenderResult {
  const missing = new Set<string>();
  const resolve = buildResolver(account, locationProfile, franchiseProfile, extras, (ph) =>
    missing.add(ph),
  );
  const output = template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = resolve(name);
    if (value === null) {
      missing.add(name);
      return whole;
    }
    return value;
  });
  return { output, missingPlaceholders: [...missing] };
}
